import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import WorkspaceDataTable from '../../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import { revisionColumn, useMasterRevision } from '../../../components/workspace/useMasterRevision';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import './CustomerMaster.scss';

const inr = (paise) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
}).format((Number(paise) || 0) / 100);

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const RateMasterPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useDocumentTitle('Costing Rates');

  const load = useCallback(async () => {
    setError('');
    try {
      const response = await platformAPI.listRates();
      setRows(response.data?.data?.rates || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const admin = useAdminRecordDelete({
    entity: 'rate',
    noun: 'rate',
    plural: 'rates',
    reload: load,
    label: (row) => row.designation
  });
  const revision = useMasterRevision({ entity: 'rate' });

  const tableRows = useMemo(
    () => rows.map((row) => ({
      ...row,
      basisLabel: row.rateBasis === 'monthly' ? 'Monthly' : 'Hourly',
      purposeLabel: row.purpose === 'effort_cost' ? 'Internal effort cost' : row.purpose === 'charge_out' ? 'Charge-out' : 'Needs classification',
      amountLabel: inr(row.amountPaise),
      effectiveLabel: formatDate(row.effectiveFrom)
    })),
    [rows]
  );

  const columns = useMemo(
    () => [
      { key: 'designation', label: 'Designation', mobileTitle: true },
      { key: 'purposeLabel', label: 'Purpose', width: '180px' },
      { key: 'basisLabel', label: 'Basis', width: '120px' },
      { key: 'amountLabel', label: 'Amount', width: '140px' },
      { key: 'effectiveLabel', label: 'Effective from', width: '150px' },
      revisionColumn,
      {
        key: 'isActive',
        label: 'Status',
        width: '120px',
        render: (value) => (
          <span className={`cust-status cust-status--${value ? 'active' : 'inactive'}`}>
            {value ? 'Active' : 'Inactive'}
          </span>
        )
      }
    ],
    []
  );

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Navigate to="/apps/tender" replace />;
  }

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="cust-page">
      <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters')}>
        <BiArrowBack />
        Masters
      </button>
      <WorkspacePageHeader
        title="Costing Rates"
        help="rates"
        subtitle="Internal effort cost is used for approved hours. Charge-out is used for proposed client pricing. An unclassified rate is not used until someone classifies it."
        actions={(
          <>
            <span className="cust-page__count">Total records {rows.length}</span>
            <Button size="sm" variant="primary" onClick={() => navigate('/apps/tender/masters/rates/new')}>
              New
            </Button>
          </>
        )}
      />
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {admin.notice}
      <WorkspaceDataTable
        downloadPermission="masters.download"
        title="Costing Rates"
        data={tableRows}
        columns={columns}
        searchPlaceholder="Search designation or basis…"
        pageSize={12}
        onView={(row) => navigate(`/apps/tender/masters/rates/${row._id}`)}
        onEdit={(row) => navigate(`/apps/tender/masters/rates/${row._id}/edit`)}
        {...admin.tableProps}
        rowActions={revision.wrapActions(admin.wrapActions(() => [
          { type: 'view', label: 'View' },
          { type: 'edit', label: 'Edit' }
        ]))}
      />
      {admin.modal}
      {revision.modals}
    </div>
  );
};

export default RateMasterPage;
